import { useState } from 'react'
import {
  useReservationRatesLazyQuery,
  useOriginsQuery,
  useReservationStatesQuery,
  useReservationsCreationQuery,
  useAdditionalServicesQuery,
  useResourceTypesGroupQuery,
  RqQueryFilter,
} from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { ReservationRatesPerOccupancy, ResourceTypePerOccupancy } from './reservation-group.types'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

export function useReservationGroupUpsertCRUD(propertyId: string, reservedResourceId?: string) {
  const { id: tenantId, currency } = useTenant()
  const showError = useErrorSnackBar()
  const [loadingReservationRates, setLoadingReservationRates] = useState(false)
  const [reservationRates, setReservationRates] = useState<ReservationRatesPerOccupancy>(undefined)

  const { data: resourceTypeGroupData, loading: resourceTypeGroupLoading } = useResourceTypesGroupQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        timeResources: false,
      },
    },
  })

  const [reservationRatesLazyQuery] = useReservationRatesLazyQuery({
    onError: showError,
  })
  const resourceTypeGroup = resourceTypeGroupData?.resourceTypes ?? []
  const resourceTypePerOccupancy = resourceTypeGroup.reduce((perOccupancy, resourceType) => {
    const occupancy = resourceType.maxAdults ?? resourceType.persons

    if (perOccupancy[occupancy]) {
      perOccupancy[occupancy].push(resourceType.id)
    } else {
      perOccupancy[occupancy] = [resourceType.id]
    }

    return perOccupancy
  }, {} as ResourceTypePerOccupancy)

  async function reservationRatesQuery(filter: Omit<RqQueryFilter, 'resources'>) {
    setLoadingReservationRates(true)
    setReservationRates(undefined)
    for (const occupancy in resourceTypePerOccupancy) {
      const result = await reservationRatesLazyQuery({
        variables: {
          filter: {
            ...filter,
            resources: [
              {
                adults: Number(occupancy),
                quantity: 1,
              },
            ],
          },
        },
      })

      if (result.data) {
        let resourceTypeIds: undefined | string[]
        if (filter.rateId) {
          resourceTypeIds = result.data?.rateQuery?.rates?.[0]?.resourceRates?.map((resourceRate) => resourceRate.resourceTypeId) ?? []
        }

        setReservationRates((prev) => ({
          ...(prev ?? {}),
          [occupancy]: {
            ...result.data,
            rateQuery: {
              ...(result.data?.rateQuery ?? {}),
              resources:
                resourceTypeIds !== undefined ? result.data?.rateQuery?.resources?.filter((resource) => resourceTypeIds?.includes(resource.resourceType.id)) ?? [] : result.data?.rateQuery.resources,
            },
          },
        }))
      }
    }

    setLoadingReservationRates(false)
  }

  const { data: additionalServiceResult, loading: loadingAdditionalServices } = useAdditionalServicesQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
      },
    },
  })

  const { data: reservationsResult, loading: loadingReservations } = useReservationsCreationQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: propertyId,
        id: { eq: reservedResourceId },
      },
    },
    skip: !reservedResourceId,
  })

  const { data: originsResult, loading: loadingOrigins } = useOriginsQuery({
    variables: {
      tenantId: tenantId,
      pms: true,
    },
  })

  const { data: reservationStatesResult, loading: loadingReservationStates } = useReservationStatesQuery({ variables: { tenantId: tenantId } })

  const loading = loadingReservationRates || loadingOrigins || loadingReservationStates || loadingReservations || loadingAdditionalServices || resourceTypeGroupLoading

  return { loading, currency, reservationRatesQuery, reservationRates, reservationStatesResult, originsResult, reservationsResult, additionalServiceResult, resourceTypePerOccupancy }
}
